import React, { useEffect, useCallback, useRef } from "react";

interface InfiniteScrollProps {
  children: React.ReactNode;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  loadingComponent?: React.ReactNode;
  endMessage?: React.ReactNode;
  threshold?: number;
  className?: string | null;
}

export default function InfiniteScroll({
  children,
  onLoadMore,
  hasMore,
  isLoading,
  loadingComponent,
  endMessage,
  threshold = 400,
  className,
}: InfiniteScrollProps) {
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const isCallingRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    isLoadingRef.current = isLoading;
    // Reset calling flag when loading completes
    if (!isLoading) {
      isCallingRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const handleScroll = useCallback(async () => {
    // Use refs for immediate check (more reliable than state)
    if (isLoadingRef.current || !hasMoreRef.current || isCallingRef.current) {
      return;
    }

    // Get the scrollable container (main element from MainLayout)
    const scrollContainer = document.getElementById("main-container");

    if (!scrollContainer) return;

    const scrollTop = scrollContainer.scrollTop || 0;
    const scrollHeight = scrollContainer.scrollHeight || 0;
    const clientHeight = scrollContainer.clientHeight || window.innerHeight;

    // Load more when user scrolls to within threshold of the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const thresholdValue = threshold ?? 500;

    // Check if we're near the bottom
    if (distanceFromBottom <= thresholdValue && distanceFromBottom >= -200) {
      // Final check with refs before calling - prevent duplicate calls
      if (
        !isLoadingRef.current &&
        hasMoreRef.current &&
        !isCallingRef.current
      ) {
        try {
          isCallingRef.current = true;
          await onLoadMore();
        } catch (err) {
          isCallingRef.current = false;
        }
      }
    }
  }, [onLoadMore, threshold]);

  useEffect(() => {
    // Find the scrollable main element
    const scrollContainer = document.getElementById("main-container");

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      handleScroll();
      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    } else {
      // Fallback to window if main element not found
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <div className={className || ""}>
      {children}
      {isLoading && (
        <div className="py-3 text-center">
          {loadingComponent || <div>Loading…</div>}
        </div>
      )}
      {!hasMore && <div className="py-3 text-center">{endMessage}</div>}
    </div>
  );
}
